import ComponentManager from '@/Management/ComponentManager';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import WindowViewModel from '@/ViewModels/Controls/WindowViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import ImageViewModelBuilder from '@/Builder/ImageViewModelBuilder';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class CrontabWindowViewModel extends WindowViewModel {

    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');
    private patternValues: Array<string>;

    public value: KnockoutObservable<string>;
    public quickSelectLabel: LabelViewModel;
    public quickSelectButtons: Array<ButtonViewModel>;
    public cronJobBoxes: Array<NumericTextBoxViewModel>;
    public customLabel: LabelViewModel;
    public setJobButton: ButtonViewModel;
    public resetButton: ButtonViewModel;

    public constructor(protected params: any) {
        super(params);

        this.patternValues = this.paramOrDefault(params.patternValues, []);
        this.cronJobBoxes = this.paramOrDefault(params.cronJobBoxes, []);
        this.value = this.observableOrDefault(params.value, ''),

        this.quickSelectLabel = this.createLabel('BACKGROUND_PROCESS_CRONTAB_QUICKSELECT_QUICKSLECT_LABEL');
        this.customLabel = this.createLabel('BACKGROUND_PROCESS_CRONTAB_QUICKSELECT_CUSTOM_LABEL');
        this.quickSelectButtons = this.createQuickSetButtons();
        this.setJobButton = this.createSetButton();
        this.resetButton = this.createResetButton();
    }

    /**
     * creates a label with an given ergName
     * @param ergNameString
     */
    private createLabel(ergNameString: string): LabelViewModel {
        return new LabelViewModelBuilder()
            .withErgName(ergNameString)
            .build();
    }

    /**
     * creates all quick-set buttons
     */
    private createQuickSetButtons(): Array<ButtonViewModel> {
        let list = [];
        let prefix = 'BACKGROUND_PROCESS_CRONTAB_QUICKSELECT'
        let ergNames = [
            `${prefix}_YEAR`,
            `${prefix}_MONTH`,
            `${prefix}_WEEK`,
            `${prefix}_DAY`,
            `${prefix}_HOUR`,
            `${prefix}_MINUTE`,
        ];

        for (let i = 0; i < ergNames.length; i++) {
            let ergName = ergNames[i];
            if (ergName.indexOf('YEAR') >= 0) {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('A', '1')))
            } else if (ergName.indexOf('MONTH') >= 0) {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('M', '1')))
            } else if (ergName.indexOf('WEEK') >= 0) {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('W', '1')))
            } else if (ergName.indexOf('DAY') >= 0) {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('d', '1')))
            } else if (ergName.indexOf('HOUR') >= 0) {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('h', '1')))
            } else {
                list.push(this.createQuickSetButton(ergName, this.setQuickSetValue('m', '1')))
            }
        }

        return list;
    }

    /**
     * creates a ButtonViewModel for the quick-selection
     * @param ergName
     * @param onClicked
     */
    private createQuickSetButton(ergName: string, onClicked: Function): ButtonViewModel {
        let locatorVal = ergName.split('_');

        return new this.ButtonViewModel({
            onClick: onClicked,
            label: new LabelViewModelBuilder()
                .withErgName(ergName)
                .build()
            ,
            locator: LocatorHelper.buildLocator(this.locator, locatorVal[locatorVal.length - 1])
        });
    }

    /**
     * sets the value in the correct field in popup
     * @param quickSet
     * @param interval
     */
    private setQuickSetValue(quickSet: string, interval: string): Function {
        return () => {
            this.cronJobBoxes.forEach(box => box.resetValue());
            let found = this.cronJobBoxes.filter(numeric => numeric.label.caption.peek() == quickSet)
            found[0].value(interval);
        }
    }

    /**
     * creates a ButtonViewModel to set the given pattern into the main-control
     */
    private createSetButton(): ButtonViewModel {
        return new this.ButtonViewModel({
            onClick: () => {
                let valid = true;
                for (let i = 0; i < this.cronJobBoxes.length; i++) {
                    if (!this.cronJobBoxes[i].isValid()) {
                        valid = false;
                        break;
                    }
                }

                if (valid) {
                    let pattern = this.createProcessPattern();
                    this.isOpen(false);
                    this.value(pattern);
                }
            },
            label: new LabelViewModelBuilder()
                .withErgName('BACKGROUND_PROCESS_CRONTAB_PATTERN')
                .build()
            ,
            image: new ImageViewModelBuilder()
                .withSource(GlobalConfiguration.inlineImages.WINDOW_OK)
                .build()
            ,
            locator: LocatorHelper.buildLocator(this.locator, 'setPattern')
        });
    }

    /**
     * creates a ButtonViewModel to reset all values
     */
    private createResetButton(): ButtonViewModel {
        return new this.ButtonViewModel({
            onClick: () => {
                this.cronJobBoxes.forEach(box => box.resetValue());
            },
            label: new LabelViewModelBuilder()
                .withErgName('reset')
                .build()
            ,
            image: new ImageViewModelBuilder()
                .withSource(GlobalConfiguration.inlineImages.RESET)
                .build()
            ,
            locator: LocatorHelper.buildLocator(this.locator, 'resetPattern')
        });
    }

    /**
     * creates the repetition pattern by the values which are given by the input fields
     */
    private createProcessPattern(): string {
        let values = [];

        for (let i = 0; i < this.cronJobBoxes.length; i++) {
            let value = this.cronJobBoxes[i].value();
            if (!String.isNullOrEmpty(value) && parseInt(value) > 0)
                values.push(this.cronJobBoxes[i].value() + this.patternValues[i]);
        }

        return values.join(',');
    }
}

ComponentManager.setViewModel('CrontabWindowViewModel', CrontabWindowViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');